import React from "react";
import { Users, TrendingUp, BarChart3, Award, Target, ShieldAlert } from "lucide-react";
import type { OrgStats } from "../types";

interface MetricCardsProps {
  stats: OrgStats;
}

export const MetricCards: React.FC<MetricCardsProps> = ({ stats }) => {
  const seatLimit = stats.seat_limit || 0;
  const usedSeats = stats.total_members || 0;
  const seatPct = seatLimit > 0 ? Math.min(100, Math.round((usedSeats / seatLimit) * 100)) : 0;
  
  const isNearLimit = seatPct >= 85 && seatPct < 100;
  const isAtLimit = seatLimit > 0 && usedSeats >= seatLimit;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
      {/* Featured Card: Seat Utilization */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between sm:col-span-2 lg:col-span-2">
        <div>
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Seat Utilization</span>
            <div className={`p-2 rounded-xl ${isAtLimit ? "bg-rose-50 text-rose-600" : isNearLimit ? "bg-amber-50 text-amber-600" : "bg-indigo-50 text-indigo-600"}`}>
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-slate-900">{usedSeats}</span>
            {seatLimit > 0 ? (
              <span className="text-xs font-semibold text-slate-500">/ {seatLimit} total seats</span>
            ) : (
              <span className="text-xs text-slate-400 font-medium">unlimited seats</span>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-1 font-medium">
            Active team members currently enrolled in your organization
          </p>
        </div>

        {seatLimit > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between text-[11px] font-semibold mb-1.5">
              <span className={isAtLimit ? "text-rose-600 font-bold" : isNearLimit ? "text-amber-600 font-bold" : "text-slate-600"}>
                {seatPct}% allocated
              </span>
              <span className="text-slate-400">
                {Math.max(0, seatLimit - usedSeats)} seats available
              </span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isAtLimit
                    ? "bg-rose-500"
                    : isNearLimit
                    ? "bg-amber-500"
                    : "bg-indigo-600"
                }`}
                style={{ width: `${seatPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Active Learners */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between text-slate-500 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Active Learners</span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-emerald-600">{stats.active_learners}</div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 font-medium">Engaged in courses this month</p>
      </div>

      {/* Avg Quiz Score */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between text-slate-500 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Avg Quiz Score</span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <BarChart3 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-purple-600">{stats.avg_quiz_score}%</div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 font-medium">Across all module tests</p>
      </div>

      {/* Sponsored Tracks */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between text-slate-500 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Sponsored Tracks</span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
              <Award className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-amber-600">{stats.sponsored_courses?.length || 0}</div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 font-medium">Enterprise training catalog</p>
      </div>

      {/* Competency Goal Progress */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between text-slate-500 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Competency</span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <Target className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-blue-600">{stats.competency_progress}%</div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2 font-medium">Target proficiency achieved</p>
      </div>

      {/* Weak Skills Tags (if present) */}
      {stats.weak_skills && stats.weak_skills.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs sm:col-span-2 lg:col-span-6">
          <div className="flex items-center justify-between text-slate-500 mb-2.5">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-rose-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Identified Skill Gaps Across Teams
              </span>
            </div>
            <span className="text-[11px] text-slate-400 font-medium">Automated AI Diagnostic</span>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {stats.weak_skills.map((skill: string, i: number) => (
              <span
                key={i}
                className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 shadow-2xs"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
